import React, { useEffect, useMemo, useState } from 'react';
import { FormField, getPrefillFormValues, InputValue, isEmail } from './donate-form-utils';
import { DonateFormContext } from './DonateFormContext';

export interface DonateFormConfig {
    legacyUrl: string;
    charityKey: string;
    isPayPalEnabled: boolean;
    isAppleGooglePayEnabled: boolean;
    stripePublishableKey: string;
}

interface DonateFormContextProviderProps {
    formFields: { [key in string]: FormField };
    config: DonateFormConfig;
    children: React.ReactNode | React.ReactNode[];
}

const urlQuery = new URLSearchParams(window.location.search);

const getFormConfigValues = (formFields: {[key in string]: FormField}) => {
    const hiddenFields = Object.entries(formFields).filter((x) => x[1].type === 'hidden');
    const parsedHiddenFields = hiddenFields.reduce((acc, curr) => ({...acc, [curr[0]]: curr[1].fieldInputProps.value}), {});

    return parsedHiddenFields;
}

const defaultFormValues = {
    source_url: window.location.href,
    terms: 'true',
}

const DonateFormContextProvider = ({ formFields, config, children }: DonateFormContextProviderProps) => {
    const formConfigValues = useMemo(() => getFormConfigValues(formFields), []);
    const prefillValues = useMemo(() => getPrefillFormValues(urlQuery, formFields), []);
    const requiredFields = useMemo(() => (
        Object.values(formFields)
            .filter(({ fieldInputProps }) => fieldInputProps.required !== undefined && fieldInputProps.type !== 'hidden')
            .map((field) => field.fieldInputProps.name!)
    ), []);
    const [formState, setFormState] = useState<{ [key in string]: InputValue }>({ ...defaultFormValues, ...formConfigValues, ...prefillValues });
    const [isFormReady, setIsFormReady] = useState(false);

    const updateFormState = (name: string, value: string) => {
        setFormState((prev) => ({ ...prev, [name]: value }));
    }

    useEffect(() => {
        const { min, max } = formFields.amount.fieldInputProps;
        const { amount, email } = formState;
        const allRequiredFieldsFilled = requiredFields.every((field) => formState[field]);
        const amountWithinLimits = amount !== undefined && Number(amount) >= Number(min) && (max === undefined || Number(amount) <= Number(max));
        const emailIsFormatted = email !== undefined && isEmail(email as string);

        setIsFormReady(allRequiredFieldsFilled && amountWithinLimits && emailIsFormatted);
    }, [formState]);

    const terms = Boolean(formState.terms);

    return (
        <DonateFormContext.Provider value={{ formState, updateFormState, canSubmitForm: isFormReady && terms, config }}>
            {children}
        </DonateFormContext.Provider>
    );
}

export default DonateFormContextProvider;
